import { useCallback, useMemo, useState } from "react";
import {
  EditableTable,
  EditableTableField,
  EditableTableRow,
} from "@/v2/components/editable-table";
import { JSONSchema7 } from "json-schema";
import { QueryParams, PathParams } from "@/v2/entities/curl-request";
import { Tabs, TabItem } from "@/v2/components/tabs/Tabs";

const fields: EditableTableField[] = [
  {
    key: "key",
    label: "Key",
    type: "text",
    placeholder: "Parameter key",
    required: true,
  },
  {
    key: "value",
    label: "Value",
    type: "text",
    placeholder: "Parameter value",
    required: true,
  },
  {
    key: "description",
    label: "Description",
    type: "text",
    placeholder: "Description (optional)",
    required: false,
  },
];

const paramTabs: TabItem[] = [
  { value: "query", label: "Query Parameters" },
  { value: "path", label: "Path Parameters" },
];

function getDescription(schema: JSONSchema7, key: string): string | undefined {
  const property = schema?.properties?.[key];

  if (typeof property === "boolean" || property == null) {
    return;
  }

  return property.description;
}

const defaultSchema: JSONSchema7 = {
  type: "object",
  properties: {},
  required: [],
};

interface ParamsProps {
  queryParams?: QueryParams;
  queryParamsSchema?: JSONSchema7;
  pathParams?: PathParams;
  pathParamsSchema?: JSONSchema7;
  onQueryParamsChange: (queryParams: QueryParams) => void;
  onQueryParamsSchemaChange: (queryParamsSchema: JSONSchema7) => void;
  onPathParamsChange: (pathParams: PathParams) => void;
  onPathParamsSchemaChange: (pathParamsSchema: JSONSchema7) => void;
}

export function Params({
  queryParams,
  queryParamsSchema = defaultSchema,
  pathParams,
  pathParamsSchema = defaultSchema,
  onQueryParamsChange,
  onQueryParamsSchemaChange,
  onPathParamsChange,
  onPathParamsSchemaChange,
}: ParamsProps) {
  const [activeTab, setActiveTab] = useState<"query" | "path">("query");

  // Convert queryParams to table format
  const queryTableValue: EditableTableRow[] = useMemo(() => {
    if (!queryParams) {
      return [];
    }

    return Object.entries(queryParams).map(([key, value]) => ({
      id: `query_${key}_${Date.now()}`,
      key,
      value: Array.isArray(value) ? value.join(", ") : value,
      description: getDescription(queryParamsSchema, key),
      enabled: true,
    }));
  }, [queryParams, queryParamsSchema]);

  // Convert pathParams to table format
  const pathTableValue: EditableTableRow[] = useMemo(() => {
    if (!pathParams) {
      return [];
    }

    return Object.entries(pathParams).map(([key, value]) => ({
      id: `path_${key}_${Date.now()}`,
      key,
      value,
      description: getDescription(pathParamsSchema, key),
      enabled: true,
    }));
  }, [pathParams, pathParamsSchema]);

  // Convert table changes back to queryParams format
  const handleQueryTableChange = useCallback(
    (newRows: EditableTableRow[]) => {
      // Only include rows that are enabled AND have a non-empty key
      const enabledParams = newRows.filter(
        (row) => row.enabled && row.key && row.key.trim() !== ""
      );

      const newParamsSchema: JSONSchema7 = {
        type: "object",
        properties: {},
        required: [],
      };

      const queryParamsObj = enabledParams.reduce((acc, row) => {
        newParamsSchema.properties![row.key] = {
          type: "string",
          description: row.description,
        };
        newParamsSchema.required!.push(row.key);

        // Handle array values (comma-separated)
        const value = row.value;
        if (value.includes(",")) {
          acc[row.key] = value.split(",").map((v: string) => v.trim());
        } else {
          acc[row.key] = value;
        }
        return acc;
      }, {} as Record<string, string | string[]>);

      onQueryParamsChange(queryParamsObj);
      onQueryParamsSchemaChange(newParamsSchema);
    },
    [onQueryParamsChange, onQueryParamsSchemaChange]
  );

  // Convert table changes back to pathParams format
  const handlePathTableChange = useCallback(
    (newRows: EditableTableRow[]) => {
      // Only include rows that are enabled AND have a non-empty key
      const enabledParams = newRows.filter(
        (row) => row.enabled && row.key && row.key.trim() !== ""
      );

      const newParamsSchema: JSONSchema7 = {
        type: "object",
        properties: {},
        required: [],
      };

      const pathParamsObj = enabledParams.reduce((acc, row) => {
        newParamsSchema.properties![row.key] = {
          type: "string",
          description: row.description,
        };
        newParamsSchema.required!.push(row.key);

        acc[row.key] = row.value;
        return acc;
      }, {} as Record<string, string>);

      onPathParamsChange(pathParamsObj);
      onPathParamsSchemaChange(newParamsSchema);
    },
    [onPathParamsChange, onPathParamsSchemaChange]
  );

  return (
    <div className="h-full">
      <Tabs
        variant="data"
        tabs={paramTabs}
        value={activeTab}
        onValueChange={(value: string) =>
          setActiveTab(value as "query" | "path")
        }
      />

      <div className="mt-4">
        {activeTab === "query" && (
          <EditableTable
            value={queryTableValue}
            onChange={handleQueryTableChange}
            fields={fields}
            title="Query Parameters"
            addButtonText="Add Query Parameter"
            emptyStateText="No query parameters added yet"
            showToggle={true}
          />
        )}

        {activeTab === "path" && (
          <EditableTable
            value={pathTableValue}
            onChange={handlePathTableChange}
            fields={fields}
            title="Path Parameters"
            addButtonText="Add Path Parameter"
            emptyStateText="No path parameters added yet"
            showToggle={true}
          />
        )}
      </div>
    </div>
  );
}
